import React, { useState, useEffect, useMemo } from 'react';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Spinner, Table, Tabs, Tab, Form, InputGroup, Card, Row, Col } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import { Navigate } from 'react-router-dom';
import PageContainer from '../../layout/PageContainer';

// Sub-component for the Risk Model Configurator
const RiskModelConfigurator = ({ initialModel, customers, onSave }) => {
    // This component now manages its own "draft" state
    const [draftModel, setDraftModel] = useState(initialModel);

    // Update the draft if the initial model changes (e.g., after a save)
    useEffect(() => {
        setDraftModel(initialModel);
    }, [initialModel]);

    const handleFactorChange = (factorType, key, value) => {
        const updatedFactors = { ...draftModel.factors };
        updatedFactors[factorType][key] = parseInt(value, 10) || 0;
        setDraftModel({ ...draftModel, factors: updatedFactors });
    };

    const impactPreview = useMemo(() => {
        if (!draftModel) return { current: {}, new: {} }; // Guard against initial render
        const currentDistribution = { High: 0, Medium: 0, Low: 0 };
        const newDistribution = { High: 0, Medium: 0, Low: 0 };

        customers.forEach(customer => {
            // Calculate static score using the ORIGINAL model prop
            const currentOccupationScore = initialModel.factors.occupations[customer.occupation] || 0;
            const currentNationalityScore = initialModel.factors.nationalities[customer.nationality] || 0;
            const currentKycScore = currentOccupationScore + currentNationalityScore;
            
            if (currentKycScore > 30) currentDistribution.High++;
            else if (currentKycScore > 10) currentDistribution.Medium++;
            else currentDistribution.Low++;

            // Calculate new static score using the DRAFT model state
            const newOccupationScore = draftModel.factors.occupations[customer.occupation] || 0;
            const newNationalityScore = draftModel.factors.nationalities[customer.nationality] || 0;
            const newKycScore = newOccupationScore + newNationalityScore;

            if (newKycScore > 30) newDistribution.High++;
            else if (newKycScore > 10) newDistribution.Medium++;
            else newDistribution.Low++;
        });

        return { current: currentDistribution, new: newDistribution };
    }, [draftModel, customers, initialModel]);

    if (!draftModel) return <Spinner animation="border" />;

    return (
        <Row>
            <Col md={7}>
                <h4>Customizable Risk Factors</h4>
                <p>Define the points assigned to different KYC attributes. These values are used to calculate the "Static KYC Score" for each customer.</p>
                <h5>Occupation Risk</h5>
                {Object.entries(draftModel.factors.occupations).map(([key, value]) => (
                    <InputGroup className="mb-2" key={key}>
                        <InputGroup.Text style={{width: '200px'}}>{key}</InputGroup.Text>
                        <Form.Control type="number" value={value} onChange={e => handleFactorChange('occupations', key, e.target.value)} />
                    </InputGroup>
                ))}
                <h5 className="mt-4">Nationality Risk</h5>
                {Object.entries(draftModel.factors.nationalities).map(([key, value]) => (
                     <InputGroup className="mb-2" key={key}>
                        <InputGroup.Text style={{width: '200px'}}>{key}</InputGroup.Text>
                        <Form.Control type="number" value={value} onChange={e => handleFactorChange('nationalities', key, e.target.value)} />
                    </InputGroup>
                ))}
                <div className="text-end mt-3">
                    <Button onClick={() => onSave(draftModel)}>Save Risk Model</Button>
                </div>
            </Col>
            <Col md={5}>
                <Card bg="light">
                    <Card.Header as="h5">Live Impact Preview</Card.Header>
                    <Card.Body>
                        <p>This preview shows how your changes would re-categorize customers' static KYC risk if you save the model.</p>
                        <h6>Current Distribution</h6>
                        <p>High: {impactPreview.current.High} | Medium: {impactPreview.current.Medium} | Low: {impactPreview.current.Low}</p>
                        <h6 className="mt-3">New Distribution (If Saved)</h6>
                        <p className="fw-bold">
                            High: <span className={impactPreview.new.High > impactPreview.current.High ? 'text-danger' : ''}>{impactPreview.new.High}</span> | 
                            Medium: {impactPreview.new.Medium} | 
                            Low: <span className={impactPreview.new.Low > impactPreview.current.Low ? 'text-success' : ''}>{impactPreview.new.Low}</span>
                        </p>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

// Sub-component for Periodic Reassessment
const RiskReassessment = () => {
    // ... This component's code is identical to the previous version and is correct.
    const [isAssessing, setIsAssessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const { user } = useAuth();

    const runReassessment = async () => {
        setIsAssessing(true);
        setProgress(0);
        const [customers, riskModel] = await Promise.all([
            dbService.getAll('customers'),
            dbService.getById('risk_models', 'customer_risk_model'),
        ]);

        for (let i = 0; i < customers.length; i++) {
            const customer = customers[i];
            const occupationScore = riskModel.factors.occupations[customer.occupation] || 0;
            const nationalityScore = riskModel.factors.nationalities[customer.nationality] || 0;
            const newKycScore = occupationScore + nationalityScore;

            const updatedCustomer = {
                ...customer,
                riskBreakdown: {
                    ...customer.riskBreakdown,
                    kycScore: newKycScore,
                    total: newKycScore + customer.riskBreakdown.txnScore,
                }
            };
            await dbService.update('customers', updatedCustomer);
            setProgress(((i + 1) / customers.length) * 100);
        }

        toast.success(`Completed risk reassessment for all ${customers.length} customers.`);
        await dbService.addLog({ user: user.name, action: 'Ran Risk Reassessment', details: `Recalculated static scores for ${customers.length} customers.` });
        setIsAssessing(false);
    };

    return (
        <div>
            <h4>Periodic Customer Risk Reassessment</h4>
            <p>Simulate a periodic (e.g., monthly) batch job that recalculates the "Static KYC Score" for every customer in the database based on the currently saved Risk Model.</p>
            <Button onClick={runReassessment} disabled={isAssessing}>
                {isAssessing ? <Spinner as="span" size="sm" /> : 'Run Full Customer Risk Reassessment'}
            </Button>
            {isAssessing && <div className="mt-3">Processing... {progress.toFixed(0)}% complete.</div>}
        </div>
    );
};


// Main Component
const RiskManagementStudio = () => {
    const [riskModel, setRiskModel] = useState(null);
    const [customers, setCustomers] = useState([]);
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const { user } = useAuth();

    const fetchData = async () => {
        const [model, customerData] = await Promise.all([
            dbService.getById('risk_models', 'customer_risk_model'),
            dbService.getAll('customers')
        ]);
        setRiskModel(model);
        setCustomers(customerData);
    };
    useEffect(() => { fetchData(); }, []);

    const handleSaveModel = async (updatedModel) => {
        setRiskModel(updatedModel); // Optimistically update UI
        await dbService.update('risk_models', updatedModel);
        await dbService.addLog({ user: user.name, action: 'Updated Risk Model', details: 'Customer risk scoring factors were changed.' });
        toast.success("Risk Model saved successfully!");
    };
    
    if (isPermissionsLoading || !riskModel) return <div className="text-center p-5"><Spinner animation="border" /></div>;
    if (!permissions.isAdmin) return <Navigate to="/dashboard" />;

    return (
        <PageContainer title="Risk Management Studio">
            <p>Configure the system's risk scoring engine and run periodic risk assessment processes.</p>
            <Tabs defaultActiveKey="configurator" className="mt-3">
                <Tab eventKey="configurator" title="Risk Model Configurator">
                    <div className="p-3 border border-top-0">
                        <RiskModelConfigurator initialModel={riskModel} onSave={handleSaveModel} customers={customers} />
                    </div>
                </Tab>
                <Tab eventKey="reassessment" title="Periodic Reassessment">
                    <div className="p-3 border border-top-0"><RiskReassessment /></div>
                </Tab>
            </Tabs>
        </PageContainer>
    );
};

export default RiskManagementStudio;